/**
 * Global fetch interceptor that automatically injects the Authorization
 * header on all same-origin API requests. Call `installFetchInterceptor()`
 * once at app boot (e.g. in main.tsx).
 */
import { getAccessToken, clearTokens } from './auth'

let installed = false

export function installFetchInterceptor(): void {
  if (installed) return
  installed = true

  const originalFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const token = getAccessToken()

    // Only inject for relative URLs (same-origin API calls)
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url
    const isRelative = url.startsWith('/') && !url.startsWith('//')

    // Skip auth injection for public auth endpoints (login/register/refresh)
    const isAuthEndpoint = url === '/auth/login' || url === '/auth/register' || url === '/auth/refresh'

    let res: Response
    if (token && isRelative && !isAuthEndpoint) {
      const headers = new Headers(init?.headers)
      if (!headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      res = await originalFetch(input, { ...init, headers })
    } else {
      res = await originalFetch(input, init)
    }

    // Auto-logout on 401 for non-auth endpoints
    if (res.status === 401 && isRelative && !isAuthEndpoint && token) {
      clearTokens()
      window.location.href = '/login'
    }

    return res
  }
}
